import React, { useState } from 'react';
import { Cloud, Zap, Menu, X, CheckCircle, ArrowRight, Search, Workflow, Building2, TrendingUp, Shield, RefreshCw, DollarSign, Target, Settings, Rocket, BarChart3 } from 'lucide-react';


export default function DraveoWebsite() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    _gotcha: '' // honeypot field for spam protection
  });
  const [formStatus, setFormStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);



  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic form validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setFormStatus('Please fill in all required fields.');
      setTimeout(() => setFormStatus(''), 3000);
      return;
    }

    // Honeypot check (for bots)
    if (formData._gotcha && formData._gotcha.trim() !== "") {
      setFormStatus('Spam detected. Submission blocked.');
      return;
    }

    setIsSubmitting(true);
    setFormStatus('');

    try {
      // Replace with your Formcarry endpoint
      const FORMCARRY_ENDPOINT = 'https://formcarry.com/s/NwRlipBX2TD';
      const response = await fetch(FORMCARRY_ENDPOINT, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message: formData.message,
          _gotcha: formData._gotcha || '' // honeypot
        })
      });

      if (response.ok) {
        setFormStatus('Thank you! Your message has been sent successfully. We\'ll get back to you soon.');
        setFormData({ name: '', email: '', company: '', message: '', _gotcha: '' });
      } else {
        setFormStatus('Sorry, there was an error sending your message. Please try again or contact us directly.');
      }
    } catch (error) {
      setFormStatus('Sorry, there was an error sending your message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setFormStatus(''), 8000);
    }
  };

  const solutions = [
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Well-Architected Workshop",
      description: "Comprehensive AWS Well-Architected Framework reviews customized for startups and SMBs to ensure your architecture follows best practices."
    },
    {
      icon: <Workflow className="w-8 h-8" />,
      title: "DevOps & Infrastructure Automation",
      description: "Automate deployments, CI/CD pipelines, and manage infrastructure as code for efficiency and reliability."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Performance Optimization",
      description: "Optimize your AWS infrastructure for peak performance and cost efficiency."
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "Cloud Migration",
      description: "Seamlessly migrate your infrastructure to AWS with minimal downtime and maximum efficiency."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Security & Compliance",
      description: "Implement robust security measures, compliance frameworks, and best practices to protect your cloud infrastructure and data."
    },
    {
      icon: <RefreshCw className="w-8 h-8" />,
      title: "Application Modernization",
      description: "Transform legacy applications into modern, cloud-native solutions using microservices, containers, and serverless architectures."
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: "Cost Optimization",
      description: "Analyze and optimize your AWS spending with right-sizing, reserved instances, and cost-effective architecture recommendations."
    }
  ];

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-lg fixed w-full z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <img src="/logo/draveo-logo.svg" alt="Draveo" className="h-10" />
              </div>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('solutions')} className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium py-2 px-3 rounded-lg hover:bg-blue-50">
                Solutions
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 transition-colors duration-200 font-medium py-2 px-3 rounded-lg hover:bg-blue-50">
                About
              </button>
              <button onClick={() => scrollToSection('contact')} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg">
                Contact
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors">
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 pt-4 pb-6 space-y-2">
              <button onClick={() => scrollToSection('solutions')} className="block w-full text-left px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium">
                Solutions
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium">
                About
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium mt-2">
                Contact
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-16 bg-gradient-to-br from-white via-blue-50 to-white text-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-8 leading-tight">
              Transform Your Business with <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">Cloud Native Solutions</span>
            </h1>
            <p className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto text-gray-600 leading-relaxed">
            We help businesses accelerate digital transformation with <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent font-semibold">AWS cloud-native solutions</span>. Our team designs, builds, and optimizes cloud solutions for peak performance, scalability, and cost efficiency.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-12 py-6 rounded-2xl text-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105 shadow-2xl focus:outline-none focus:ring-4 focus:ring-blue-300"
                style={{ boxShadow: '0 20px 40px 0 rgba(37,99,235,0.25)' }}
              >
                Get Started Today
                <ArrowRight className="inline ml-3 w-6 h-6 align-middle" />
              </button>
              <button 
                onClick={() => scrollToSection('solutions')}
                className="bg-white text-blue-600 px-12 py-6 rounded-2xl text-xl font-bold border-2 border-blue-600 hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-300"
              >
                View Solutions
              </button>
            </div>
          </div>
        </div>
        <div className="w-full h-24 bg-gradient-to-t from-gray-50 to-transparent"></div>
      </section>

      {/* How We Work Section */}
      <section id="how-we-work" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How We Work</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our proven methodology to deliver exceptional cloud solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold mb-3 text-gray-900">Assess & Strategize</h4>
              <p className="text-gray-600 text-sm leading-relaxed">We begin by understanding your business and technical landscape. Our team evaluates your current infrastructure, identifies cloud readiness, and aligns your goals with a strategic transformation roadmap. We provide clear cost-benefit analysis and ROI projections so you can make confident decisions.</p>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Settings className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold mb-3 text-gray-900">Architect & Design</h4>
              <p className="text-gray-600 text-sm leading-relaxed">We design cloud-native solutions tailored to your growth ambitions. This includes crafting migration strategies, establishing robust security and compliance frameworks, and building architectures optimized for scalability and performance from the ground up.</p>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Rocket className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold mb-3 text-gray-900">Implement & Migrate</h4>
              <p className="text-gray-600 text-sm leading-relaxed">We execute seamless migrations with minimal disruption to your operations. Leveraging infrastructure as code and automation, we modernize applications, implement containerization, and deliver a stable, efficient cloud environment ready to support your business.</p>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold mb-3 text-gray-900">Optimize & Scale</h4>
              <p className="text-gray-600 text-sm leading-relaxed">Our partnership continues beyond deployment. We provide ongoing monitoring, performance tuning, and cost optimization to maximize your AWS investment. Through auto-scaling, capacity planning, and governance best practices, we keep your infrastructure agile and prepared for growth.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Solutions</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive AWS cloud services tailored to your business needs with a security-first approach
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8">
            {solutions.map((solution, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group text-center border border-gray-100 w-80 max-w-sm">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                  <div className="text-white">
                    {solution.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{solution.title}</h3>
                <p className="text-gray-600 leading-relaxed">{solution.description}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent"></div>
            <div className="relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-3xl font-bold mb-8">Why Choose Draveo?</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center mr-4 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-lg">Team with AWS and startup/SMB experience</span>
                    </div>
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center mr-4 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-lg">We know the pain points of building and scaling startups and SMBs</span>
                    </div>
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center mr-4 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-lg">Support for both MVP/POC and large-scale production launches</span>
                    </div>
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center mr-4 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-lg">Hands-on, personalized guidance for early-stage growth</span>
                    </div>
                  </div>
                </div>
                <div className="text-center lg:text-right">
                  <div className="inline-block bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <ArrowRight className="w-10 h-10 text-white" />
                    </div>
                    <div className="text-4xl font-bold mb-2">Startup & SMB Focus</div>
                    <div className="text-blue-100 text-lg">Empowering startups and SMBs on their cloud journey</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">About Draveo</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your trusted partner in AWS cloud transformation
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="bg-white p-8 rounded-2xl shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  At Draveo, we specialize in helping businesses harness the full power of Amazon Web Services. 
                  Our team of certified AWS experts brings years of experience in cloud architecture, migration, 
                  and optimization to every project.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Approach</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We understand that every business is unique, which is why we provide customized cloud solutions 
                  that align with your specific goals and requirements. From startups and SMBs to enterprise organizations, 
                  our employees have helped companies across various industries transform their operations through strategic cloud adoption.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 rounded-3xl p-10 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent"></div>
              <div className="relative z-10">
                <h3 className="text-3xl font-bold mb-8">Our Expertise</h3>
                <div className="space-y-6">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-4"></div>
                    <span className="text-lg">AWS Certified Solutions Architects</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-4"></div>
                    <span className="text-lg">DevOps & Infrastructure Modernization</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-4"></div>
                    <span className="text-lg">Security & Compliance Standards</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-4"></div>
                    <span className="text-lg">Cost Optimization Strategies</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-4"></div>
                    <span className="text-lg">24/7 Support & Monitoring</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ready to transform your business with AWS? Let's discuss your cloud journey.
            </p>
          </div>
          
          <div className="flex justify-center">
            {/* Contact Form Only */}
            <div className="w-full max-w-2xl">
              <div className="bg-gray-50 p-10 rounded-3xl shadow-sm border border-gray-100">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-gray-900 placeholder-gray-500"
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-gray-900 placeholder-gray-500"
                      placeholder="your.email@company.com"
                    />
                  </div>

                {/* Honeypot field for spam protection (hidden from users) */}
                <div style={{ display: 'none' }}>
                  <label htmlFor="_gotcha">Leave this field blank</label>
                  <input
                    type="text"
                    id="_gotcha"
                    name="_gotcha"
                    value={formData._gotcha}
                    onChange={handleInputChange}
                    autoComplete="off"
                    tabIndex="-1"
                  />
                </div>
                {/* Reminder: Replace the Formcarry endpoint in handleSubmit with your actual form ID */}
                
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-3">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-gray-900 placeholder-gray-500"
                      placeholder="Your company name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-3">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 resize-none"
                      placeholder="Tell us about your project or how we can help..."
                    ></textarea>
                  </div>
                  
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`w-full py-4 px-8 rounded-xl font-bold text-lg transition-all duration-300 transform shadow-lg ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:scale-105 hover:shadow-xl text-white'
                    }`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                
                  {formStatus && (
                    <div className={`p-6 border rounded-xl ${
                      formStatus.includes('error') || formStatus.includes('Sorry')
                        ? 'bg-red-50 border-red-200 text-red-800'
                        : formStatus.includes('required')
                        ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                        : 'bg-green-50 border-green-200 text-green-800'
                    }`}>
                      <p className="font-medium">{formStatus}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Sora', 'Inter', Arial, sans-serif", color: '#0063dc' }}>
                Draveo
              </h3>
              <p className="text-xl text-gray-300">Your Cloud Partner</p>
            </div>
            <div className="border-t border-gray-700 pt-8">
              <p className="text-sm text-gray-400">
                © 2025 Draveo. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}